<template>
  <Dialog
    v-model:visible="isVisible"
    :modal="true"
    :closable="true"
    :dismissable-mask="true"
    class="col-12"
    @show="onShow"
  >
    <template #header>
      <div class="content-header">
        <h3>{{ $t('dialog.editbarcode.title') }}</h3>
        <p>{{ $t('dialog.editbarcode.subtitle') }}</p>
      </div>
    </template>
    <div class="col">
      <LineIdAdvancedFilter
        v-model:stakeholder-id="lineIdFilter.filters.stakeholderId.value"
        v-model:mainVenue="lineIdFilter.filters.mainVenue.value"
        v-model:spectrumService="lineIdFilter.filters.spectrumService.value"
        v-model:tuningRangeFrom="lineIdFilter.filters.tuningRangeFrom.value"
        v-model:tuningRangeTo="lineIdFilter.filters.tuningRangeTo.value"
        v-model:duplexHigh="lineIdFilter.filters.duplexHigh.value"
        v-model:duplexLow="lineIdFilter.filters.duplexLow.value"
        :stakeholder="barcode.stakeholder"
        replace-licence-period
        @validate="getLineIds"
        @reset="resetLineIdFilter"
      />
      <LineIdTable
        v-model:sortField="lineIdFilter.pageable.sortField"
        v-model:sortOrder="lineIdFilter.pageable.sortOrder"
        v-model:filters="lineIdFilter.filters"
        v-model:rows="lineIdFilter.pageable.size"
        :loading="isLoading"
        paginator
        paginator-position="both"
        :show-stakeholder-id="false"
        show-dates
        show-channel-bandwidth
        show-spectrum-service
        :show-associated="false"
        selection-mode="multiple"
        :value="lines"
        :total-records="totalLineIdCount"
        @sort="getLineIds"
        @filter="getLineIds"
        @page="onLineIdPageChange($event)"
      >
        <template #before>
          <Column
            v-if="isAgent"
            :header="$t('entity.control.associated')"
            :exportable="false"
            style="width:200px"
            field="barcodeRangeId"
            :filter-match-mode-options="barcodeRangeIdMatchMods"
          >
            <template #filter />
            <template #body="{data}">
              <Checkbox
                :model-value="hasControl(data)"
                :binary="true"
                @input="(value) => associate(data, value)"
              />
            </template>
          </Column>
        </template>
      </LineIdTable>
    </div>
    <div class="w-full text-right mt-2 p-4">
      <Button
        :label="$t('action.cancel')"
        type="button"
        icon="pi pi-times"
        class="p-button-rounded p-button-secondary p-button-outlined mr-2"
        @click="close"
      />
      <Button
        :label="$t('action.crud.update')"
        type="submit"
        icon="pi pi-plus"
        class="p-button-rounded p-button-outlined float-right"
        @click="toggleControls"
      />
    </div>
  </Dialog>
</template>

<script>
import Dialog from "primevue/dialog";
import LineIdTable from "@/components/lineId/LineIdTable";
import Column from "primevue/column";
import Checkbox from "primevue/checkbox";
import LineIdAdvancedFilter from "@/components/lineId/LineIdAdvancedFilter.vue";
import {CONTACT_ROLE} from "@/shared/constants";
import useControlFilter from "@/composables/filters/useControlFilter";
import {reactive} from "vue";
import useLineIdFilter from "@/composables/filters/useLineIdFilter";
import {FilterMatchMode} from "primevue/api";
import Button from "primevue/button";

export default {
  name: 'BarcodeEditDialog',
  components: {
    LineIdTable,
    Dialog,
    Column,
    Checkbox,
    LineIdAdvancedFilter,
    Button
  },
  inject: {
    appointmentId: {
      from: 'appointmentId',
      default: null
    },
    immediateTaggingId: {
      from: 'immediateTaggingId',
      default: null
    },
  },
  props: {
    barcode: {
      type: Object,
      required: true
    },
    visible: {
      type: Boolean,
      required: true
    }
  },
  emits: ['update:visible', 'edit'],
  setup() {
    const controlFilter = reactive(useControlFilter());
    controlFilter.pageable.size = 0;

    const lineIdFilter = reactive(useLineIdFilter());

    return {
      controlFilter,
      lineIdFilter
    }
  },
  data() {
    return {
      controls: [],
      lines: [],
      totalLineIdCount: 0,
      associationsByLineId: new Map(),
      associationChanges: new Map(),

      isLoading: false
    }
  },
  computed: {
    isAgent() {
      return this.$store.state.auth.user.role !== CONTACT_ROLE;
    },
    isVisible: {
      get() {
        return this.visible
      },
      set(value) {
        this.$emit('update:visible', value)
      }
    },
    barcodeRangeIdMatchMods() {
      return [
        {label: this.$t('entity.lineId.associated'), value: FilterMatchMode.EQUALS},
        {label: this.$t('entity.lineId.notassociated'), value: FilterMatchMode.NOT_EQUALS},
      ]
    },
  },
  methods: {
    async onShow() {
      this.associationsByLineId = new Map();
      this.associationChanges = new Map();
      this.lineIdFilter.pageable.page = 0;
      this.lineIdFilter.pageable.size = this.$store.getters['setting/getSizePerPageLineId'];
      this.setBaseFilter();
      this.getLineIds();
      if (this.isAgent) {
        this.getControls()
      }
    },
    setBaseFilter() {
      this.lineIdFilter.filters.barcodeRangeId.value = this.barcode.id;
      this.lineIdFilter.filters.stakeholder.value = this.barcode.stakeholder.id;
      this.controlFilter.filters.barcodeId.value = this.barcode.id;
    },
    resetLineIdFilter() {
      this.lineIdFilter.reset();
      this.setBaseFilter();
      this.getLineIds();
    },
    async getLineIds() {
      this.isLoading = true
      try {
        const params = this.lineIdFilter.getQueryParams(this.$store.getters['setting/getSizePerPageLineId']);
        const result = await this.$lineIdService.fetchLinesPagingAndSorting(params);
        this.lines = result.content;
        this.totalLineIdCount = result.totalElements;
      } catch (e) {
        console.error(e);
        this.$emitter.emit('toast', {severity: 'error', message: 'error.global'});
      } finally {
        this.isLoading = false
      }
    },

    async getControls() {
      const result = await this.$controlService.getAll(this.controlFilter.getQueryParams(this.$store.getters['setting/getSizePerPageBarcode']));
      this.controls = result.content;

      this.associationsByLineId = new Map();
      this.controls.forEach(control => {
        this.associationsByLineId.set(control.lineId.id, control.deletionDate == null);
      });
    },

    async toggleControls() {
      try {
        await this.$controlService.toggleControlsByBarcodeRangeAndLineIds(this.barcode.id, Object.fromEntries(this.associationChanges), this.appointmentId, this.immediateTaggingId);
        this.$emitter.emit('toast', {severity: 'success', message: 'dialog.editbarcode.togglecontrolsuccess'});
        this.close();
      } catch (e) {
        const errors = e.response.data.map(error => this.$t(error)).join(', ')
        this.$emitter.emit('toast', {severity: 'error', message: errors})
      }
    },
    onLineIdPageChange(event) {
      this.lineIdFilter.pageable.page = event.page;
      this.getLineIds();
    },
    hasControl(lineId) {
      if (this.associationChanges.has(lineId.id)) {
        return this.associationChanges.get(lineId.id);
      } else if (this.associationsByLineId.has(lineId.id)) {
        return this.associationsByLineId.get(lineId.id);
      } else {
        return false;
      }
    },
    associate(data, value) {
      this.associationChanges.set(data.id, value);
    },
    close() {
      this.isVisible = false;
    }
  }
}
</script>
